'use client'

import { useEffect, useRef, useState } from 'react'

import { CatalogNavigationDto } from '@marketplace-web/catalog/catalog-data'

const generateNavStackReverse = (
  category: CatalogNavigationDto,
  targetId: number,
  path: Array<number> = [],
): Array<number> | undefined => {
  const categoryId = Number(category.id)
  const newPath = [...path, categoryId]

  if (categoryId === targetId) {
    return newPath
  }

  for (let index = 0; index < category.catalogs.length; index += 1) {
    const child = category.catalogs[index]!
    const result = generateNavStackReverse(child, targetId, newPath)
    if (result) {
      return result
    }
  }

  return undefined
}

const generateNavStack = (
  categories: Array<CatalogNavigationDto>,
  targetId: number,
): Array<number> => {
  for (let index = 0; index < categories.length; index += 1) {
    const category = categories[index]!
    const path = generateNavStackReverse(category, targetId)
    if (path) {
      return path
    }
  }

  return [targetId]
}

const useNavStack = (
  categories: Array<CatalogNavigationDto> | undefined,
  catalogIds: Array<number>,
): readonly [Array<number>, (navStack: Array<number>) => void] => {
  const [navStack, setNavStack] = useState<Array<number>>([])
  const navStackRef = useRef(navStack)
  navStackRef.current = navStack

  // update navStack whenever categories or catalogIds change
  useEffect(() => {
    if (!categories || !catalogIds.length) {
      setNavStack([])

      return
    }

    const targetId: number = catalogIds[0] ?? -1
    const newNavStack = generateNavStack(categories, targetId)
    setNavStack(newNavStack)
  }, [categories, catalogIds])

  return [navStack, setNavStack] as const
}

export default useNavStack
